import React, { useState, useCallback, useContext } from 'react';
import { withTranslation } from 'react-i18next';
import { Tooltip, Upload, message } from 'antd';
import { GridContainer, GridItem, DownloadButton, PinNavBar, ComplexContainer } from './styles';
import ZoomDropdown from '../ZoomDropdown';
import { DOCUMENT_TEMPLATES_MENUBAR, docGenerationUpgradeMessages } from './translationValues';
import { generatePDF } from '../../../utils/services/complexDocumentsAPI';
import { generatePDFPayload } from '../complexDocumentHelper';
import {
  useDocumentContext,
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
} from '../../../context/complexDocumentContext';
import { deepCopy, toZipFile } from '../../../utils/javascript';
import {
  DYNAMIC_TABLE_BLOCK,
  FOOTER,
  HEADER,
  STANDALONE_BLOCK,
  STATIC_TABLE_BLOCK,
  TEXT_BLOCK,
} from '../../../utils/complexDocumentConstants';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';
import { ENTERPRISE_ENV } from '../../../utils/helper';
import { BusinessLogic } from '../../../context/businessLogicContext';
import {
  modifyBlockContents,
  modifyDynamicTableBlockContents,
  modifyTextBlockContents,
  modifyVariableContract,
  extractVariables,
  listAllUsedFonts,
  EXPORT_FILE_NAME,
  importVariables,
  partialMatchKey,
  modifyStaticTableBlockContents,
  exportRelations,
  importRelationships,
  extractRelationship
} from '../../../utils/importHelper';
import { FontsContext } from '../../../context/fontsContext';
import { useSessionContext } from '../../../context/sessionContext';
import axiosCall from '../../../utils/services/interceptor';
import { uploadFonts } from '../../../utils/services/uploadAPI';
import { useDefaultStyleContext } from '../../../context/complexDefaultStyleContext';
import { zipSync, unzipSync, strToU8, strFromU8 } from 'fflate';
import { uploadGoogleFonts } from '../../../utils/services/fontsAPI';
import { getAllLanguage } from '../../../utils/services/languageApi';
import { changeLanguage } from '../../../utils/services/complexDocumentsAPI';

const NAMESPACES = ['DOCUMENT_TEMPLATES_MENUBAR'];

const checkUserAccess = (userType, roles, userDetails = {}) => {
  if (roles) {
    return (
      userDetails?.userType === userType && userDetails.roles.some(role => roles.includes(role))
    );
  } else {
    return userDetails?.userType === userType;
  }
};

const MenuBar = props => {
  const { showVersions, t } = props;

  const [isPinned, setIsPinned] = useState(false);
  const [isDropDownVisible, toggleDropDownVisible] = useState(false);
  const { businessLogics } = useContext(BusinessLogic);
  const { fontMeta, handleFonts } = useContext(FontsContext);

  const artboardSelector = useCallback(
    store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES],
    [],
  );
  const [allVariables] = useDocumentContext(artboardSelector);

  const contextSelector = useCallback(store => store, []);
  const [documentContext, store] = useDocumentContext(contextSelector);

  const customSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN], []);
  const [editable, updateArtboard] = useArtboardContext(customSelector);

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);
  
  const getCurrentDesign = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [CurrentDesign] = useArtboardContext(getCurrentDesign);

  const getDefaultStyleSelector = useCallback(store => store, []);
  const [documentStyle] = useDefaultStyleContext(getDefaultStyleSelector);

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `MenuBar`);

  const toggleIsPinned = useCallback(() => {
    setIsPinned(previousValue => !previousValue);
  }, []);

  const exportTemplate = async () => {
    const flowIds = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS];
    const globalVariables = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES];
    const variablesToExport = new Set();
    const relationships = [];
    updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: true });
    try {
      const exportedFlows = flowIds.map(flowId => {
        const contextFlow = documentContext[flowId];
        const {
          margin,
          width,
          height,
          backgroundColor,
          pageNumber,
          size,
          orientation,
          flowName
        } = contextFlow;
        const blockIds = contextFlow[`${flowId}-0`];
        const exportedBlocks = blockIds.flatMap(blockId => {
          const block = deepCopy(documentContext[blockId]);

          if (block.blockType === STANDALONE_BLOCK) {
            const standaloneBlocks = block.contents.map(contentUUID => {
              const block = deepCopy(documentContext[contentUUID]);
              extractVariables(block.variableContract, globalVariables).forEach(v => {
                variablesToExport.add(v);
              });
              const extractedRelationships = extractRelationship(block.variableContract, globalVariables);
              relationships.push(...extractedRelationships);
              delete block.blockId;
              delete block.orderNumber;
              block.isStandalone = false;
              if (block.blockType === DYNAMIC_TABLE_BLOCK) {
                const tableProperties = block.style.tableProperties;
                const tableChildBlock = [tableProperties.topBlock, tableProperties.bottomBlock];
                const dtoList = tableChildBlock.filter(element => element !== undefined);
                block.blockDTOList = dtoList.map(childBlockUUID => {
                  const childBlock = deepCopy(documentContext[childBlockUUID]);
                  extractVariables(childBlock.variableContract, globalVariables).forEach(v => {
                    variablesToExport.add(v);
                  });
                  childBlock.isStandalone = false;
                  return childBlock;
                });
              }
              return block;
            });
            return standaloneBlocks;
          } else if (block.blockType === DYNAMIC_TABLE_BLOCK) {
            const tableProperties = block.style.tableProperties;
            const tableChildBlock = [tableProperties.topBlock, tableProperties.bottomBlock];
            const dtoList = tableChildBlock.filter(element => element !== undefined);
            block.blockDTOList = dtoList.map(childBlockUUID => {
              const childBlock = deepCopy(documentContext[childBlockUUID]);
              extractVariables(childBlock.variableContract, globalVariables).forEach(v => {
                variablesToExport.add(v);
              });
              childBlock.isStandalone = false;
              return childBlock;
            });
          } else if (block.blockType === STATIC_TABLE_BLOCK) {
            block.contents = block.contents.map(content => {
              if (documentContext[content.uuid]) {
                content.contents = [documentContext[content.uuid]];
              }
              return content;
            })
          }
          extractVariables(block.variableContract, globalVariables).forEach(v => {
            variablesToExport.add(v);
          });
          const extractedRelationships = extractRelationship(block.variableContract, globalVariables);
          relationships.push(...extractedRelationships);
          return block;
        });
        let exportedHeader, exportedFooter;
        if (contextFlow[HEADER]) {
          const header = deepCopy(documentContext[contextFlow[HEADER]]);
          header.isStandalone = false;
          exportedHeader = header;
          extractVariables(header.variableContract, globalVariables).forEach(v =>
            variablesToExport.add(v),
          );
        }
        if (contextFlow[FOOTER]) {
          const footer = deepCopy(documentContext[contextFlow[FOOTER]]);
          footer.isStandalone = false;
          exportedFooter = footer;
          extractVariables(footer.variableContract, globalVariables).forEach(v =>
            variablesToExport.add(v),
          );
        }
        const exportedFlow = {
          style: {
            padding: {
              top: margin[0],
              right: margin[1],
              bottom: margin[2],
              left: margin[3],
            },
            width: width,
            height: height,
            backgroundColor: backgroundColor,
            pageNumber: pageNumber ? { ...documentContext[pageNumber] } : undefined,
          },
          paperSize: size,
          paperOrientation: orientation,
          blocks: exportedBlocks,
          header: exportedHeader,
          footer: exportedFooter,
        };
        if (flowName) {
          exportedFlow.flowName = flowName;
        }
        return exportedFlow;
      });
      const relationshipsToExport = await exportRelations(relationships);

      // create zip file with json template and fonts.
      const fonts = { nonGoogleFonts: [], googleFonts: [] };
      const fontObject = {};
      const usedFontList = listAllUsedFonts();
      for (let i = 0; i < fontMeta.length; i++) {
        const font = fontMeta[i];
        if (usedFontList.includes(font.name) && font.fontType === 'uploaded-font') {
          for (let j = 0; j < font.fontFiles.length; j++) {
            const file = font.fontFiles[j];
            if (file.fontOrigin === 'upload-font') {
              const rData = { responseType: 'arraybuffer' };
              const { data } = await axiosCall('get', file.fileLink, rData, true);
              const filePathArr = file.filePath?.split(".");
              const fileExtension = filePathArr?.length ? filePathArr[filePathArr.length - 1] : 'ttf';
              const fontName = `${font.name}-${file.fontType}.${fileExtension}`;
              fontObject[fontName] = new Uint8Array(data);
              fonts.nonGoogleFonts.push(fontName);
            } else {
              const googleUploadFont = {
                file: file.fileLink,
                fontName: font.name,
                fontType: file.fontType,
              };
              fonts.googleFonts.push(googleUploadFont);
            }
          }
        }
      }
      const languagecode = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.LANGUAGE];
      let language;
      if (languagecode) {
        try {
          const languageResponse = await getAllLanguage();
          if (languageResponse.data) {
            language = languageResponse.data.find(list => list.code === languagecode);
          }
        } catch (e) {
          console.log(e);
        }
      };
      
      const documentToExport = {
        flows: exportedFlows,
        style: documentStyle,
        variables: [...variablesToExport],
        relationships: relationshipsToExport,
        fonts: fonts,
        language:language,
        summary: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.DESIGN_SUMMARY],
      };
      const content = zipSync({ [EXPORT_FILE_NAME]: strToU8(JSON.stringify(documentToExport)), 'fonts': fontObject });
      toZipFile(content, documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.DOCUMENT_NAME]);
    } catch {
      message.error(t(
        DOCUMENT_TEMPLATES_MENUBAR.DOCUMENT_EXPORT_ERROR.key,
        DOCUMENT_TEMPLATES_MENUBAR.DOCUMENT_EXPORT_ERROR.value,
      ));
    } finally {
      updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: false });
    }
  };

  const importTemplate = file => {
    const reader = new FileReader();
    reader.onload = handleTemplateImport;
    reader.readAsArrayBuffer(file);
  };

  const handleTemplateImport = async event => {
    let hasError = false;
    try {
      // unzip import file and get the contents
      const zipFile = unzipSync(new Uint8Array(event.target.result));
      const templateFile = strFromU8(zipFile[partialMatchKey(zipFile, EXPORT_FILE_NAME)]);
      const parsedFile = JSON.parse(templateFile);
      const { flows, variables, style, fonts, language, relationships, summary } = parsedFile;

      const globalVariables = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES];
      updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: true });
      await importRelationships(relationships);
      const variableIdMap = await importVariables(variables, globalVariables, store);
      const uploadedUrlsMap = new Map();
      for (let flowIndex = 0; flowIndex < flows.length; flowIndex++) {
        const flow = flows[flowIndex];
        flow.flowId = undefined;
        flow.isStandalone = false;
        const blocks = flow.blocks;
        for (let blockIndex = 0; blockIndex < blocks.length; blockIndex++) {
          const block = blocks[blockIndex];
          block.blockId = undefined;
          if (block.type === DYNAMIC_TABLE_BLOCK) {
            for (let dIndex = 0; dIndex < block.blockDTOList?.length; dIndex++) {
              const subBlock = block.blockDTOList[dIndex];
              subBlock.contents = await modifyBlockContents(subBlock.contents, variableIdMap, uploadedUrlsMap);
              subBlock.variableContract = modifyVariableContract(
                subBlock.variableContract,
                variableIdMap,
              );
            }
            block.contents = modifyDynamicTableBlockContents(block.contents, variableIdMap);
          } else if (block.type === TEXT_BLOCK) {
            block.contents = await modifyTextBlockContents(block.contents, variableIdMap);
          } else if (block.type === STATIC_TABLE_BLOCK) {
            block.contents = await modifyStaticTableBlockContents(block.contents, variableIdMap, uploadedUrlsMap);
          } else {
            block.contents = await modifyBlockContents(block.contents, variableIdMap, uploadedUrlsMap);
          }
          block.variableContract = modifyVariableContract(block.variableContract, variableIdMap);
        }
        if (flow.header) {
          const header = flow.header;
          header.blockId = undefined;
          header.contents = await modifyBlockContents(header.contents, variableIdMap, uploadedUrlsMap);
          header.variableContract = modifyVariableContract(header.variableContract, variableIdMap);
        }
        if (flow.footer) {
          const footer = flow.footer;
          footer.blockId = undefined;
          footer.contents = await modifyBlockContents(footer.contents, variableIdMap, uploadedUrlsMap);
          footer.variableContract = modifyVariableContract(footer.variableContract, variableIdMap);
        }
      }

      // upload non-google fonts
      const nonGoogleFonts = fonts?.nonGoogleFonts || [];
      for (let fIndex = 0; fIndex < nonGoogleFonts.length; fIndex++) {
        const fontFile = new Blob([zipFile[partialMatchKey(zipFile, `fonts/${nonGoogleFonts[fIndex]}`)]]);
        await uploadFonts(fontFile, nonGoogleFonts[fIndex]);
      }
      // upload google fonts
      const googleFonts = fonts?.googleFonts || [];
      const googleFontsPayload = { fonts: [] };
      for (let fIndex = 0; fIndex < googleFonts.length; fIndex++) {
        googleFontsPayload.fonts.push(googleFonts[fIndex]);
      }
      if (googleFontsPayload.fonts.length > 0) {
        await uploadGoogleFonts(googleFontsPayload);
      }
      handleFonts(); // refetch all the fonts and append the font styles to head.

      setTimeout(async () => {
        try {
          const callback = async (status = 'failed') => {
            if (status === 'success' && language) {
              try {
                const payload = {
                  id: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
                  language: language,
                  type: CurrentDesign,
                };
               const response =  await changeLanguage(payload);
              } catch (e) {
                console.log(e);
              }
            }
          };
          store.set({[COMPLEX_DOCUMENT_CONTEXT_KEYS.AUTOSAVE_CALLBACK]: callback});
          store.replaceDocument(flows, style, () => {}, summary);
        } catch (e) {
          message.error(t(
            DOCUMENT_TEMPLATES_MENUBAR.DOCUMENT_IMPORT_ERROR.key,
            DOCUMENT_TEMPLATES_MENUBAR.DOCUMENT_IMPORT_ERROR.value,
          ));
        } finally {
          updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: false });
        }
      }, 1000);
    } catch (e) {
      hasError = true;
      message.error(t(
        DOCUMENT_TEMPLATES_MENUBAR.DOCUMENT_IMPORT_ERROR.key,
        DOCUMENT_TEMPLATES_MENUBAR.DOCUMENT_IMPORT_ERROR.value,
      ));
    } finally {
      if (hasError) {
        updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: false });
      }
    }
  };

  return (
    <>
      <ComplexContainer > 
      {!showVersions && (
        <GridContainer
          complexMenubar={ENTERPRISE_ENV}
          className={`enable ${isPinned || isDropDownVisible ? 'visible' : ''}`}
        >
          <PinNavBar>
            <div>
              <span className={isPinned ? 'pinned' : ''} onClick={toggleIsPinned}>
                <svg viewBox="0 0 32 32">
                  <g id="surface1">
                    <path
                      style={{}}
                      d="M 20.53125 2.5625 L 19.84375 3.5 L 14.9375 10.1875 C 12.308594 9.730469 9.527344 10.472656 7.5 12.5 L 6.78125 13.1875 L 12.09375 18.5 L 4 26.59375 L 4 28 L 5.40625 28 L 13.5 19.90625 L 18.8125 25.21875 L 19.5 24.5 C 21.527344 22.472656 22.269531 19.691406 21.8125 17.0625 L 28.5 12.15625 L 29.4375 11.46875 Z M 20.78125 5.625 L 26.375 11.21875 L 20.15625 15.78125 L 19.59375 16.1875 L 19.78125 16.84375 C 20.261719 18.675781 19.738281 20.585938 18.59375 22.1875 L 9.8125 13.40625 C 11.414063 12.261719 13.324219 11.738281 15.15625 12.21875 L 15.8125 12.40625 L 16.21875 11.84375 Z "
                    />
                  </g>
                </svg>
              </span>
            </div>
          </PinNavBar>
          <GridItem enterprise={ENTERPRISE_ENV}> 
            {!isInteractiveDesign && (ENTERPRISE_ENV || checkUserAccess('master', undefined, userDetails)) && (
              <>
                <Tooltip title={t(
                    DOCUMENT_TEMPLATES_MENUBAR.EXPORT_DOCUMENT_LABEL.key,
                    DOCUMENT_TEMPLATES_MENUBAR.EXPORT_DOCUMENT_LABEL.value,
                  )}>
                  <DownloadButton
                    icon="export"
                    type="primary"
                    size="large"
                    onClick={exportTemplate}
                  />
                </Tooltip>
                {editable ? (
                    <Upload accept=".zip" action={importTemplate}>
                      <Tooltip
                        title={t(
                          DOCUMENT_TEMPLATES_MENUBAR.IMPORT_DOCUMENT_LABEL.key,
                          DOCUMENT_TEMPLATES_MENUBAR.IMPORT_DOCUMENT_LABEL.value,
                        )}
                      >
                        <DownloadButton type="primary" size="large" icon="import" />
                      </Tooltip>
                    </Upload>
                  ) : null}
              </>
            )}
            <ZoomDropdown dropDownVisible={toggleDropDownVisible} />
          </GridItem>
        </GridContainer>
      )}
      </ComplexContainer>
    </>
  );
};

export default withTranslation(NAMESPACES)(MenuBar);
